import PropTypes from 'prop-types'
import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Cell, Pie, PieChart, Tooltip } from 'recharts'
import { getFontSize } from '../../../helpers/fontSize'
import { valToPercent } from '../../../helpers/value'
import { UseTypeFontSize } from '../../hooks/useTypeFontSize'
import { Element } from '../../html/element'
import { deleteChartData, selectChartData, setChartData } from '../store'
import { ChartLabel } from './ChartLabel'
import { ChartTooltipContent } from './ChartTooltipContent'
import { CHART_TOOLTIP, LABEL_OPTIONS_DEFAULT } from './constants'
import { getLabelSize } from './helper'

export const PieChartContainer = ({
  chartId,
  children,
  data,
  width,
  height,
  cx,
  cy,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  labelTooltip,
  formatNameTooltip,
  formatValueTooltip,
  labelCenter,
  isStrokeActive,
  isTooltipActive,
  isLabelActive,
  renderCustomActiveShape,
  renderCustomTooltip,
  labelOptions,
  onPieClick,
  labelLineColor,
  defaultActiveIndex,
  propsTooltip,
  isDisableActiveIndex,
  label,
  locale,
}) => {
  const dispatch = useDispatch()
  const typeFontSize = UseTypeFontSize()
  const fontSize = getFontSize(12, typeFontSize)
  const activeIndex = useSelector(
    selectChartData(chartId, (data) => data.activeIndex),
  )

  useEffect(() => {
    if (chartId) {
      dispatch(setChartData({ chartId }))
      return () => {
        dispatch(deleteChartData({ chartId }))
      }
    }
  }, [chartId])

  // Get data
  const labelSize = useMemo(() => {
    return getLabelSize(
      data[0],
      1,
      0,
      fontSize,
      labelOptions?.renderCustomLabel,
    )
  }, [labelOptions?.renderCustomLabel])

  const getOuterRadius = useMemo(() => {
    if (!isLabelActive) return '100%'

    if (typeof outerRadius === 'string' && outerRadius.includes('%')) {
      const halfHeight = height / 2
      const halfWidth = width / 2
      const heightOuterRadiusPercent = (1 - labelSize.height / halfHeight) * 100
      const labelLineMaxLength =
        (labelOptions?.firstLabelLineLength ||
          LABEL_OPTIONS_DEFAULT.firstLabelLineLength) +
        Math.max(
          labelOptions?.secondLabelLineLength ||
            LABEL_OPTIONS_DEFAULT.secondLabelLineLength,
          labelSize.width,
        )
      const widthOuterRadiusPercent = (1 - labelLineMaxLength / halfWidth) * 100
      const outerRadiusProp = parseInt(outerRadius.replace('%', ''))

      return (
        Math.min(
          ...[
            outerRadiusProp,
            heightOuterRadiusPercent,
            widthOuterRadiusPercent,
          ],
        ) + '%'
      )
    }

    if (typeof outerRadius === 'number') {
      return outerRadius
    }

    return '50%'
  }, [width, height, labelSize, outerRadius, isLabelActive])

  // Actions
  const onPieOver = (_, index) => {
    if (chartId) {
      dispatch(setChartData({ chartId, activeIndex: index }))
    }
  }

  const onPieLeave = () => {
    if (chartId) {
      dispatch(setChartData({ chartId, activeIndex: undefined }))
    }
  }

  // Render
  const renderCustomizedLabel = (props) => {
    const { x, cy, outerRadius } = props

    // Download jpg only apply inline style
    return (
      <g>
        <foreignObject
          x={x - outerRadius}
          y={cy - outerRadius}
          height={outerRadius * 2}
          width={outerRadius * 2}
        >
          <div
            style={{
              textAlign: 'center',
              width: '100%',
              height: '100%',
              position: 'relative',
            }}
          >
            <Element
              style={{
                ...labelCenter?.style,
                fontSize: 12,
                fontWeight: 400,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              {labelCenter.customLabel
                ? labelCenter.customLabel()
                : labelCenter.title || ''}
            </Element>
          </div>
        </foreignObject>
      </g>
    )
  }

  const renderLabel = (props) => {
    if (renderCustomActiveShape || !isLabelActive) return ''
    return (
      <ChartLabel
        data={data}
        width={width}
        height={height}
        labelLineColor={labelLineColor}
        {...labelOptions}
        {...props}
      />
    )
  }

  const renderTooltip = () => {
    if (!isTooltipActive) return null
    return (
      <Tooltip
        wrapperStyle={CHART_TOOLTIP.wrapperStyle}
        labelStyle={{ ...CHART_TOOLTIP.labelStyle, fontSize }}
        cursor={CHART_TOOLTIP.cursor}
        allowEscapeViewBox={{ x: true, y: true }}
        isAnimationActive={false}
        content={
          renderCustomTooltip || (
            <ChartTooltipContent
              data={data}
              activeIndex={activeIndex}
              label={labelTooltip}
              formatNameTooltip={formatNameTooltip}
              formatValueTooltip={formatValueTooltip}
              propsTooltip={propsTooltip}
            />
          )
        }
        {...propsTooltip}
      />
    )
  }

  const renderPieLabel = (props) => {
    const { percent, cx, cy, midAngle, innerRadius, outerRadius } = props
    const RADIAN = Math.PI / 180
    const radius = innerRadius + (outerRadius - innerRadius) * 0.1
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)

    return (
      <text
        x={x}
        y={y}
        fill="#fff"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={10}
        fontWeight={600}
      >
        {/* {`${(percent * 100).toFixed(2)}%`} */}
        {valToPercent(percent, locale, false, true)}
      </text>
    )
  }

  return (
    <PieChart width={width} height={height}>
      {children}
      {labelCenter && (
        <Pie
          data={[{ name: '', value: 1 }]}
          dataKey="value"
          cx={cx}
          cy={cy}
          label={renderCustomizedLabel}
          labelLine={false}
          outerRadius={labelCenter.outerRadius || '30%'}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={labelCenter.fill || '#fff'}
          stroke={labelCenter.fill || '#fff'}
          isAnimationActive={false}
        />
      )}
      <Pie
        activeIndex={
          (activeIndex || activeIndex === 0) && !isDisableActiveIndex
            ? activeIndex
            : defaultActiveIndex
        }
        activeShape={renderCustomActiveShape}
        inactiveShape={{ fillOpacity: !isDisableActiveIndex ? 0.4 : 1 }}
        data={data}
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={getOuterRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        dataKey="value"
        label={label ? renderPieLabel : () => ''}
        labelLine={renderLabel}
        onMouseOver={onPieOver}
        isAnimationActive={false}
        onMouseLeave={onPieLeave}
        onClick={onPieClick}
      >
        {data.map((item, index) => (
          <Cell
            key={`cell-${index}`}
            fill={item.color}
            stroke={isStrokeActive ? item.color : '#fff'}
          />
        ))}
      </Pie>
      {renderTooltip()}
    </PieChart>
  )
}

PieChartContainer.propTypes = {
  chartId: PropTypes.string,
  children: PropTypes.node,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
      colorPieBorder: PropTypes.string,
    }),
  ).isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  cx: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  cy: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  innerRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  outerRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  startAngle: PropTypes.number,
  endAngle: PropTypes.number,
  isStrokeActive: PropTypes.bool,
  isTooltipActive: PropTypes.bool,
  isLabelActive: PropTypes.bool,
  labelCenter: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  labelTooltip: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  formatNameTooltip: PropTypes.func,
  formatValueTooltip: PropTypes.func,
  renderCustomActiveShape: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.node,
  ]),
  renderCustomTooltip: PropTypes.func,
  labelOptions: PropTypes.shape({
    minValueHandle: PropTypes.number,
    rangeBetweenLabelLine: PropTypes.number,
    rangeBetweenPieAndLine: PropTypes.number,
    firstLabelLineLength: PropTypes.number,
    secondLabelLineLength: PropTypes.number,
    yTextFromBottom: PropTypes.number,
    renderCustomLabel: PropTypes.func,
  }),
  onPieClick: PropTypes.func,
  labelLineColor: PropTypes.string,
  propsTooltip: PropTypes.object,
  isDisableActiveIndex: PropTypes.bool,
  label: PropTypes.bool,
}

PieChartContainer.defaultProps = {
  data: [],
  cx: '50%',
  cy: '50%',
  innerRadius: 0,
  outerRadius: '70%',
  startAngle: 90,
  endAngle: -450,
  label: false,
  labelLine: false,
  isStrokeActive: false,
  isTooltipActive: true,
  isLabelActive: true,
  labelCenter: false,
  propsTooltip: {},
  isDisableActiveIndex: false,
}
